import React from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '../utils/logger';

interface Props {
  children: React.ReactNode;
  fallbackRender?: (props: { error: Error; resetError: () => void }) => React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches React errors and reports them to Sentry
 */
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to console in development
    logger.error('React component error caught by boundary', error, {
      componentStack: errorInfo.componentStack,
      error,
    });

    // Report to Sentry
    Sentry.withScope(scope => {
      scope.setTag('error_boundary', 'true');

      // Add the component stack as context
      scope.setContext('react.component_stack', {
        componentStack: errorInfo.componentStack,
      });

      Sentry.captureException(error);
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({
          error: this.state.error as Error,
          resetError: this.resetError,
        });
      }

      // Default fallback UI
      return (
        <div className="p-6 text-center bg-white rounded-lg shadow-md border border-red-100">
          <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
          {this.state.error && (
            <div className="bg-gray-100 p-4 rounded-md text-sm text-red-800 overflow-auto max-w-full mb-4">
              {this.state.error.message}
            </div>
          )}
          <p className="text-gray-600 mb-4">We've logged this error and will look into it.</p>
          <div className="flex gap-3 justify-center">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={this.resetError}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * A Sentry-aware function component error boundary that's easier to use with hooks
 */
export const SentryErrorBoundary: React.FC<Props> = ({ children }) => {
  const beforeCapture = (scope: Sentry.Scope) => {
    scope.setTag('error_boundary', 'true');
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={beforeCapture}
      fallback={({ error, resetError }) => (
        <div className="p-6 text-center bg-white rounded-lg shadow-md border border-red-100">
          <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <div className="bg-gray-100 p-4 rounded-md text-sm text-red-800 overflow-auto max-w-full mb-4">
            {error.message}
          </div>
          <p className="text-gray-600 mb-4">We've logged this error and will look into it.</p>
          <div className="flex gap-3 justify-center">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              onClick={resetError}
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
